import React from 'react';
import { Modal, Form, Button, Row, Col, Table, InputGroup } from 'react-bootstrap';
import { Formik, FieldArray } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import { Post } from '../../../../utils/httpProvider';
import { QUATRINHLAYSOLIEU_API, CONGBOKHOAHOC_API, KEHOACHTIEPTHEO_API, NOILAYSOLIEU_API } from '../../../../utils/apiUrl';
import { objectLoaiHoatDong } from '../../../../utils/define';
import DocumentInput from '../../../../components/document-input/DocumentInput';

const schema = yup.object().shape({
    noiLay: yup.string().required('Vui lòng nhập nơi lấy số liệu'),
    tuNgay: yup.date().nullable(),
    denNgay: yup.date().min(
        yup.ref('tuNgay'),
        "Đến ngày phải lớn hơn hoặc bằng Từ ngày"
    ).nullable(),
    mauDatTyLe: yup.number()
        .typeError('Tỷ lệ phải là số')
        .min(0, 'Tỷ lệ tối thiểu 0')
        .max(100, 'Tỷ lệ tối đa 100')
        .nullable(),
    congBoKhoaHocs: yup.array().of(
        yup.object().shape({
            tenTapChi: yup.string().max(500, 'Tên tạp chí không quá 500 ký tự'),
            linkTapChi: yup.string()
        })
    ),
    soLieuDat: yup.number()
        .typeError('Số liệu đạt phải là số')
        .min(0, 'Số liệu tối thiểu 0')
        .nullable(),
    keHoachTiepTheos: yup.array().of(
        yup.object().shape({
            loaiHoatDong: yup.string(),
            thoiGian: yup.string().when('loaiHoatDong', {
                is: (val) => val && val.toString().length > 0,
                then: yup.string()
                    .required('Bắt buộc nhập thời gian dự kiến khi đã chọn hoạt động')
                    .matches(/^(0[1-9]|1[0-2])\/\d{4}$/, 'Định dạng thời gian phải là mm/yyyy'),
                otherwise: yup.string().nullable()
            })
        })
    )
});

const QuaTrinhLaySoLieuAdd = ({ hoSoId, setViewMode, refreshList }) => {
    const queryClient = useQueryClient();

    const handleCreate = async (values, { setSubmitting }) => {
        try {
            // 1. Tạo Nơi lấy số liệu
            const nlPayload = {
                hoSoNghienCuuSinhId: hoSoId,
                noiLay: values.noiLay,
                fileCuDiLay: values.fileCuDiLay,
                fileCongVan: values.fileCongVan,
                ghiChuChinhSua: values.ghiChuChinhSua,
                trangThai: 'choTiepNhan'
            };

            const nlRes = await Post(NOILAYSOLIEU_API.CREATE, null, nlPayload, false, false, true);
            if (!nlRes || (nlRes.status !== 200 && nlRes.status !== 201 && nlRes.status !== 204 && !nlRes.id)) {
                toast.error(nlRes?.message || 'Có lỗi xảy ra khi tạo Nơi lấy số liệu');
                setSubmitting(false);
                return;
            }

            // 2. Tạo Quá trình lấy số liệu
            const payload = {
                hoSoNghienCuuSinhId: hoSoId,
                tuNgay: values.tuNgay ? values.tuNgay : null,
                denNgay: values.denNgay ? values.denNgay : null,
                mauDatTyLe: (values.mauDatTyLe !== '' && values.mauDatTyLe !== null && values.mauDatTyLe !== undefined) ? parseInt(values.mauDatTyLe) : null,
                coCongBoKhoaHoc: values.coCongBoKhoaHoc,
                fileMinhChung: values.fileMinhChung
            };

            const qtRes = await Post(QUATRINHLAYSOLIEU_API.CREATE, null, payload, false, false, true);
            if (qtRes && qtRes.id) {
                const qtId = qtRes.id;
                
                if (qtId) {
                    const promises = [];

                    // 3. Tạo Công bố khoa học (nếu có)
                    if (values.coCongBoKhoaHoc && values.congBoKhoaHocs && values.congBoKhoaHocs.length > 0) {
                        const validCB = values.congBoKhoaHocs.filter(cb => cb.tenTapChi || cb.linkTapChi);
                        for (const cb of validCB) {
                            promises.push(
                                Post(CONGBOKHOAHOC_API.CREATE, null, {
                                    quaTrinhLaySoLieuId: qtId,
                                    tenTapChi: cb.tenTapChi,
                                    linkTapChi: cb.linkTapChi
                                }, false)
                            );
                        }
                    }

                    // 4. Tạo Kế hoạch tiếp theo (nếu có chọn)
                    if (values.keHoachTiepTheos && values.keHoachTiepTheos.length > 0) {
                        for (const kh of values.keHoachTiepTheos) {
                            let thang = null, nam = null;
                            if (kh.thoiGian) {
                                const parts = kh.thoiGian.split('/');
                                if (parts.length === 2) {
                                    thang = parseInt(parts[0]);
                                    nam = parseInt(parts[1]);
                                }
                            }
                            promises.push(
                                Post(KEHOACHTIEPTHEO_API.CREATE, null, {
                                    quaTrinhLaySoLieuId: qtId,
                                    loaiHoatDong: kh.loaiHoatDong,
                                    thangDuKien: thang,
                                    namDuKien: nam,
                                    soLieuDat: (values.soLieuDat !== '' && values.soLieuDat !== null && values.soLieuDat !== undefined) ? parseInt(values.soLieuDat) : null
                                }, false)
                            );
                        }
                    }

                    // Chờ tất cả promises chạy xong
                    if (promises.length > 0) {
                        await Promise.allSettled(promises);
                    }
                }
                
                toast.success('Thêm mới thành công');
                queryClient.invalidateQueries(['quaTrinhLaySoLieuData', hoSoId]);
                queryClient.invalidateQueries(['noiLaySoLieuData', hoSoId]);
                if (refreshList) refreshList();
                setViewMode('list');
            } else {
                toast.error(qtRes?.message || 'Có lỗi xảy ra khi tạo Quá trình lấy số liệu');
            }
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra trong quá trình xử lý');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={true} onHide={() => setViewMode('list')} size="xl" backdrop="static">
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>Thêm mới Quá trình lấy số liệu</Modal.Title>
            </Modal.Header>
            <Formik
                validationSchema={schema}
                onSubmit={handleCreate}
                initialValues={{
                    noiLay: '',
                    fileCuDiLay: '',
                    fileCongVan: '',
                    ghiChuChinhSua: '',
                    tuNgay: '',
                    denNgay: '',
                    mauDatTyLe: '',
                    coCongBoKhoaHoc: true,
                    congBoKhoaHocs: [{ tenTapChi: '', linkTapChi: '' }],
                    soLieuDat: '',
                    keHoachTiepTheos: [],
                    fileMinhChung: ''
                }}
            >
                {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                            {/* Section 1: Nơi lấy số liệu */}
                            <h6 className="font-weight-bold text-primary mb-3">1. Nơi lấy số liệu</h6>
                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="font-weight-bold">Nơi lấy số liệu <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="noiLay"
                                            value={values.noiLay}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Nhập nơi lấy số liệu"
                                        />
                                        {touched.noiLay && !!errors.noiLay && <div className="text-danger mt-1" style={{ fontSize: '13px' }}>{errors.noiLay}</div>}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <DocumentInput
                                            labelTitle="File cử đi lấy số liệu đính kèm"
                                            value={values.fileCuDiLay}
                                            onChangeProp={(url) => setFieldValue('fileCuDiLay', url)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <hr />

                            {/* Section 2: Thông tin lấy số liệu */}
                            <h6 className="font-weight-bold text-primary mb-3">2. Thông tin lấy số liệu</h6>
                            <Row>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Từ ngày</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="tuNgay"
                                            value={values.tuNgay}
                                            onChange={handleChange}
                                            isInvalid={touched.tuNgay && !!errors.tuNgay}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.tuNgay}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Đến ngày</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="denNgay"
                                            value={values.denNgay}
                                            onChange={handleChange}
                                            isInvalid={touched.denNgay && !!errors.denNgay}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.denNgay}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Số mẫu đã thu thập đạt tỷ lệ (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="mauDatTyLe"
                                            value={values.mauDatTyLe}
                                            onChange={handleChange}
                                            onWheel={(e) => e.target.blur()}
                                            isInvalid={touched.mauDatTyLe && !!errors.mauDatTyLe}
                                            placeholder="Nhập % đạt được"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.mauDatTyLe}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <hr />

                            {/* Section 3: Công bố khoa học */}
                            <h6 className="font-weight-bold text-primary mb-3">3. Công bố khoa học</h6>
                            <Form.Group>
                                <Form.Check
                                    inline
                                    type="radio"
                                    label="Có"
                                    name="coCongBoKhoaHoc"
                                    id="coCongBoKhoaHoc_yes"
                                    checked={values.coCongBoKhoaHoc === true}
                                    onChange={() => setFieldValue('coCongBoKhoaHoc', true)}
                                />
                                <Form.Check
                                    inline
                                    type="radio"
                                    label="Không"
                                    name="coCongBoKhoaHoc"
                                    id="coCongBoKhoaHoc_no"
                                    checked={values.coCongBoKhoaHoc === false}
                                    onChange={() => {
                                        setFieldValue('coCongBoKhoaHoc', false);
                                        setFieldValue('congBoKhoaHocs', [{ tenTapChi: '', linkTapChi: '' }]);
                                    }}
                                />
                            </Form.Group>
                            
                            {values.coCongBoKhoaHoc && (
                                <FieldArray name="congBoKhoaHocs">
                                    {({ push, remove }) => (
                                        <>
                                            {values.congBoKhoaHocs.map((item, index) => (
                                                <Row key={index} className="align-items-center mb-2">
                                                    <Col md={5}>
                                                        <Form.Group className="mb-0">
                                                            <Form.Label className={index > 0 ? "sr-only" : ""}>Tên tạp chí</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name={`congBoKhoaHocs.${index}.tenTapChi`}
                                                                value={item.tenTapChi}
                                                                onChange={handleChange}
                                                                placeholder="Nhập tên tạp chí"
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={5}>
                                                        <Form.Group className="mb-0">
                                                            <Form.Label className={index > 0 ? "sr-only" : ""}>Link tạp chí</Form.Label>
                                                            <InputGroup>
                                                                <Form.Control
                                                                    type="text"
                                                                    name={`congBoKhoaHocs.${index}.linkTapChi`}
                                                                    value={item.linkTapChi}
                                                                    onChange={handleChange}
                                                                    placeholder="Nhập link"
                                                                />
                                                                {item.linkTapChi && (
                                                                    <InputGroup.Append>
                                                                        <a 
                                                                            href={item.linkTapChi.startsWith('http') ? item.linkTapChi : `http://${item.linkTapChi}`} 
                                                                            target="_blank" 
                                                                            rel="noreferrer" 
                                                                            className="btn btn-outline-primary"
                                                                            title="Mở link"
                                                                        >
                                                                            <i className="fas fa-external-link-alt"></i>
                                                                        </a>
                                                                    </InputGroup.Append>
                                                                )}
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={2} className={index === 0 ? "mt-4" : ""}>
                                                        {values.congBoKhoaHocs.length > 1 && (
                                                            <Button variant="outline-danger" size="sm" onClick={() => remove(index)} title="Xóa dòng">
                                                                <i className="fas fa-trash"></i>
                                                            </Button>
                                                        )}
                                                    </Col>
                                                </Row>
                                            ))}
                                            <Button variant="outline-primary" size="sm" className="mt-2" onClick={() => push({ tenTapChi: '', linkTapChi: '' })}>
                                                <i className="fas fa-plus mr-1"></i> Thêm tạp chí
                                            </Button>
                                        </>
                                    )}
                                </FieldArray>
                            )}
                            <hr />

                            {/* Section 4: Kế hoạch tiếp theo */}
                            <h6 className="font-weight-bold text-primary mb-3">4. Kế hoạch tiếp theo</h6>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Lấy số liệu đạt (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="soLieuDat"
                                            value={values.soLieuDat}
                                            onChange={handleChange}
                                            onWheel={(e) => e.target.blur()}
                                            isInvalid={touched.soLieuDat && !!errors.soLieuDat}
                                            placeholder="Nhập số liệu đạt"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.soLieuDat}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="table-responsive">
                                <Table bordered>
                                    <thead className="bg-light text-center">
                                        <tr>
                                            <th className="font-weight-bold">Hoạt động</th>
                                            <th className="font-weight-bold" width="120">Tick chọn</th>
                                            <th className="font-weight-bold" width="300">Thời gian dự kiến thực hiện</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {objectLoaiHoatDong.map((hoatDong, idx) => {
                                            const khIndex = values.keHoachTiepTheos.findIndex(x => x.loaiHoatDong === hoatDong.value.toString());
                                            const isChecked = khIndex !== -1;
                                            return (
                                                <tr key={hoatDong.value}>
                                                    <td className="align-middle" style={{ fontWeight: 500 }}>{hoatDong.label}</td>
                                                    <td className="text-center align-middle">
                                                        <div className="d-flex justify-content-center">
                                                            <Form.Check 
                                                                type="checkbox"
                                                                id={`hoatDong_${hoatDong.value}`}
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setFieldValue('keHoachTiepTheos', [...values.keHoachTiepTheos, { id: null, loaiHoatDong: hoatDong.value.toString(), thoiGian: '' }]);
                                                                    } else {
                                                                        setFieldValue('keHoachTiepTheos', values.keHoachTiepTheos.filter(x => x.loaiHoatDong !== hoatDong.value.toString()));
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="align-middle">
                                                        {isChecked && (
                                                            <>
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder="mm/yyyy"
                                                                    value={values.keHoachTiepTheos[khIndex].thoiGian}
                                                                    onChange={(e) => {
                                                                        let val = e.target.value.replace(/[^0-9]/g, '');
                                                                        if (val.length >= 2) {
                                                                            let month = parseInt(val.substring(0, 2), 10);
                                                                            if (month > 12) month = 12;
                                                                            if (month === 0) month = 1;
                                                                            let monthStr = month < 10 ? '0' + month : month.toString();
                                                                            val = monthStr + val.substring(2);
                                                                        } else if (val.length === 1) {
                                                                            let month = parseInt(val, 10);
                                                                            if (month > 1) {
                                                                                val = '0' + month;
                                                                            }
                                                                        }

                                                                        if (val.length > 2) {
                                                                            val = val.substring(0, 2) + '/' + val.substring(2, 6);
                                                                        } else if (val.length === 2 && e.nativeEvent.inputType !== 'deleteContentBackward') {
                                                                            val = val + '/';
                                                                        }
                                                                        setFieldValue(`keHoachTiepTheos[${khIndex}].thoiGian`, val);
                                                                    }}
                                                                    isInvalid={touched.keHoachTiepTheos?.[khIndex]?.thoiGian && !!errors.keHoachTiepTheos?.[khIndex]?.thoiGian}
                                                                    maxLength={7}
                                                                />
                                                                {touched.keHoachTiepTheos?.[khIndex]?.thoiGian && errors.keHoachTiepTheos?.[khIndex]?.thoiGian && (
                                                                    <div className="text-danger small mt-1">{errors.keHoachTiepTheos[khIndex].thoiGian}</div>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                            
                            <hr />

                            {/* Section 5: File minh chứng */}
                            <h6 className="font-weight-bold text-primary mb-3">5. File minh chứng</h6>
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Group className="mb-0">
                                        <DocumentInput
                                            multiple={false}
                                            value={values.fileMinhChung}
                                            onChangeProp={(val) => setFieldValue('fileMinhChung', val)}
                                            labelTitle="Tải file minh chứng"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setViewMode('list')} disabled={isSubmitting}>
                                Hủy
                            </Button>
                            <Button variant="primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Đang thêm...' : 'Thêm'}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default QuaTrinhLaySoLieuAdd;
